import React, { createContext, useState, useContext, useCallback, useMemo } from 'react';

/**
 * Camera Captures Context - Manages in-memory capture list shared between
 * CameraModal and StealthCamera
 */
const CameraCapturesContext = createContext();

export function CameraCapturesProvider({ children }) {
  // Keep last 100 captures (most recent first) - array of {id, dataUrl, boundingBox, matchFailed, processing, source, clickPosition}
  const [captures, setCaptures] = useState([]);

  // Add a new capture to the list
  const addCapture = useCallback((dataUrl, source = 'camera', clickPosition = null) => {
    const newCapture = {
      id: `capture-${Date.now()}-${Math.random()}`,
      dataUrl,
      boundingBox: null,
      matchFailed: false,
      processing: false,
      source, // 'camera' or 'stealth'
      clickPosition // {x: percentage, y: percentage} where user clicked
    };
    setCaptures(prev => [newCapture, ...prev.slice(0, 99)]);
    return newCapture.id;
  }, []);

  // Update a capture by ID
  const updateCapture = useCallback((captureId, updates) => {
    setCaptures(prev => prev.map(c =>
      c.id === captureId ? { ...c, ...updates } : c
    ));
  }, []);

  // Remove a capture by ID
  const removeCapture = useCallback((captureId) => {
    setCaptures(prev => prev.filter(c => c.id !== captureId));
  }, []);

  // Clear all captures
  const clearCaptures = useCallback(() => {
    setCaptures([]);
  }, []);

  // Memoize value to prevent unnecessary re-renders
  const value = useMemo(() => ({
    captures,
    setCaptures,
    addCapture,
    updateCapture,
    removeCapture,
    clearCaptures
  }), [captures, addCapture, updateCapture, removeCapture, clearCaptures]);

  return (
    <CameraCapturesContext.Provider value={value}>
      {children}
    </CameraCapturesContext.Provider>
  );
}

export function useCameraCaptures() {
  const context = useContext(CameraCapturesContext);
  if (!context) {
    throw new Error('useCameraCaptures must be used within a CameraCapturesProvider');
  }
  return context;
}
